import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Event } from '@/types/event.types';
import type { GuestGroup } from '@/types/group.types';

interface GuestSearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  events: Event[];
  selectedEventIds: Set<number>;
  onToggleEvent: (eventId: number) => void;
  onClearEvents: () => void;
  /** Optional: groups for # autocomplete */
  groups?: GuestGroup[];
  /** Optional: results count to display */
  resultCount?: number;
}

export const GuestSearchBar: React.FC<GuestSearchBarProps> = ({
  searchQuery,
  onSearchChange,
  events,
  selectedEventIds,
  onToggleEvent,
  onClearEvents,
  groups = [],
  resultCount,
}) => {
  const { t } = useTranslation('guests');
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Extract the last #token being typed for suggestions
  const activeGroupToken = useMemo(() => {
    const lastHashIdx = searchQuery.lastIndexOf('#');
    if (lastHashIdx === -1) return null;
    const afterHash = searchQuery.substring(lastHashIdx + 1);
    if (afterHash.match(/[#|,]/)) return null;
    const hasTrailingOp = afterHash.match(/\s+(or|and)\s+$/i);
    if (hasTrailingOp) return null;
    return { index: lastHashIdx, partial: afterHash.trim().toLowerCase() };
  }, [searchQuery]);

  // Group suggestions for # search
  const groupSuggestions = useMemo(() => {
    if (!activeGroupToken || groups.length === 0) return [];
    if (activeGroupToken.partial === '') return groups;
    return groups.filter(g => g.name.toLowerCase().includes(activeGroupToken.partial));
  }, [activeGroupToken, groups]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSearchChange(value);
    const lastHashIdx = value.lastIndexOf('#');
    if (lastHashIdx >= 0 && groups.length > 0) {
      const afterHash = value.substring(lastHashIdx + 1);
      const hasTrailingOp = afterHash.match(/\s+(or|and)\s+$/i) || afterHash.match(/[#|,]/);
      setShowGroupSuggestions(!hasTrailingOp);
    } else {
      setShowGroupSuggestions(false);
    }
    setHighlightedIndex(-1);
  }, [onSearchChange, groups]);

  const selectGroupSuggestion = useCallback((groupName: string) => {
    const lastHashIdx = searchQuery.lastIndexOf('#');
    if (lastHashIdx >= 0) {
      const before = searchQuery.substring(0, lastHashIdx);
      onSearchChange(`${before}#${groupName}`);
    } else {
      onSearchChange(`#${groupName}`);
    }
    setShowGroupSuggestions(false);
    searchInputRef.current?.focus();
  }, [searchQuery, onSearchChange]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showGroupSuggestions || groupSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % groupSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? groupSuggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      selectGroupSuggestion(groupSuggestions[highlightedIndex].name);
    } else if (e.key === 'Escape') {
      setShowGroupSuggestions(false);
    }
  }, [showGroupSuggestions, groupSuggestions, highlightedIndex, selectGroupSuggestion]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        searchInputRef.current && !searchInputRef.current.contains(e.target as Node)
      ) {
        setShowGroupSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearSearch = useCallback(() => {
    onSearchChange('');
    setShowGroupSuggestions(false);
    searchInputRef.current?.focus();
  }, [onSearchChange]);

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={groups.length > 0
              ? t('searchBar.placeholders.withGroups')
              : t('searchBar.placeholders.basic')
            }
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => { if (searchQuery.lastIndexOf('#') >= 0 && groups.length > 0) setShowGroupSuggestions(true); }}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {/* Clear search button */}
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={t('searchBar.clearSearchAria')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Group suggestions dropdown */}
          {showGroupSuggestions && groupSuggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              <div className="px-3 py-1.5 text-xs text-gray-500 border-b bg-gray-50">{t('searchBar.groupsLabel')}</div>
              {groupSuggestions.map((group, idx) => (
                <button
                  key={group.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-blue-50 transition-colors ${
                    idx === highlightedIndex ? 'bg-blue-50' : ''
                  }`}
                  onMouseDown={(e) => { e.preventDefault(); selectGroupSuggestion(group.name); }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: group.color || '#6b7280' }}
                  />
                  <span className="text-sm font-medium text-gray-800">#{group.name}</span>
                  {group.is_system_group && (
                    <span className="text-[10px] text-gray-400 ml-auto">{t('searchBar.systemBadge')}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Event Checkboxes - always visible */}
      {events.length > 0 && (
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('searchBar.eventsLabel')}</span>
          {events.map(event => (
            <label
              key={event.id}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border transition-colors ${
                selectedEventIds.has(event.id)
                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedEventIds.has(event.id)}
                onChange={() => onToggleEvent(event.id)}
                className="h-3 w-3 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
              />
              {event.title}
            </label>
          ))}
          {selectedEventIds.size > 0 && (
            <button
              onClick={onClearEvents}
              className="text-xs text-blue-600 hover:underline"
            >
              {t('searchBar.clearEvents')}
            </button>
          )}
        </div>
      )}

      {/* Result count */}
      {resultCount !== undefined && (
        <div className="mt-2 text-sm text-gray-600">
          {t('searchBar.resultsCount', { count: resultCount })}
        </div>
      )}
    </div>
  );
};
